import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, RotateCcw, ExternalLink, Ban, Flag, CheckCircle2, Eye } from 'lucide-react';
import { API } from '../../App';
import { Button } from '../ui/button';

const STATUS_FILTERS = [
  { key: 'live', label: 'Live' },
  { key: 'flagged', label: 'Flagged' },
  { key: 'help', label: 'Help posts' },
  { key: 'expired', label: 'Expired' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'removed', label: 'Removed' },
  { key: 'all', label: 'All' },
];

const CAT_LABELS = {
  smoke_seen: 'Smoke seen', fire_location: 'Fire location', road_blocked: 'Road blocked',
  power_outage: 'Electricity outage', flooding: 'Flooding', fallen_tree: 'Fallen tree',
  help_needed: 'Help needed', help_offered: 'Help offered',
  animal_assistance: 'Animal assistance', general_update: 'Local update',
};

// Admin moderation panel for Community Safety Hub reports (incl. help posts).
export const AdminCommunityReportsPanel = ({ headers, towns }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('live');
  const [town, setTown] = useState('');
  const [reports, setReports] = useState([]);
  const [flaggedCount, setFlaggedCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/community-reports`, {
        params: { status, ...(town ? { town } : {}), limit: 200 },
        headers,
      });
      setReports(r.data.reports || []);
      setFlaggedCount(r.data.flaggedCount || 0);
    } catch {
      toast.error('Could not load community reports');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, town]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try {
      await axios.post(`${API}/admin/community-reports/${id}/remove`, {}, { headers });
      toast.success('Report removed');
      load();
    } catch { toast.error('Failed'); }
  };
  const restore = async (id) => {
    try {
      await axios.post(`${API}/admin/community-reports/${id}/restore`, {}, { headers });
      toast.success('Report restored');
      load();
    } catch { toast.error('Failed'); }
  };
  const suspendAuthor = async (userId, suspend) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/safety-suspend`, { suspended: suspend }, { headers });
      toast.success(suspend ? 'Author suspended from safety posting' : 'Author unsuspended');
    } catch { toast.error('Failed'); }
  };
  const permDelete = async (id) => {
    if (!window.confirm('Permanently delete this post?\nThis will also delete all replies, confirmations, disputes and related notifications. This action cannot be undone.')) return;
    try {
      await axios.delete(`${API}/community-reports/${id}`, { headers });
      toast.success('Post permanently deleted');
      load();
    } catch { toast.error('Failed'); }
  };

  return (
    <div data-testid="admin-community-reports-panel">
      <div className="flex items-center gap-2 flex-wrap mb-4">
        {STATUS_FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setStatus(f.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              status === f.key ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 border border-gray-200 hover:bg-slate-50'
            }`}
            data-testid={`admin-cr-filter-${f.key}`}
          >
            {f.label}{f.key === 'flagged' && flaggedCount > 0 ? ` (${flaggedCount})` : ''}
          </button>
        ))}
        <select
          value={town}
          onChange={e => setTown(e.target.value)}
          className="ml-auto border rounded-lg px-2 py-1.5 text-xs bg-white"
          data-testid="admin-cr-town-filter"
        >
          <option value="">All towns</option>
          {(towns || []).map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-center text-gray-400 py-10">Loading…</div>
      ) : reports.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-500" data-testid="admin-cr-empty">
          Nothing here.
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map(r => (
            <div key={r.id} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm" data-testid={`admin-cr-${r.id}`}>
              <div className="flex items-center gap-2 flex-wrap mb-1.5 text-xs">
                <span className="font-bold text-slate-800">{CAT_LABELS[r.category] || r.category}</span>
                {r.helpType && <span className="bg-gray-100 px-2 py-0.5 rounded-full text-gray-600">{r.helpType}</span>}
                <span className="text-gray-500">{r.town}{r.area ? ` · ${r.area}` : ''}</span>
                <span className={`px-2 py-0.5 rounded-full font-semibold ${
                  r.status === 'active' ? 'bg-emerald-100 text-emerald-800'
                    : r.status === 'removed' ? 'bg-red-100 text-red-800'
                    : 'bg-gray-100 text-gray-600'
                }`}>{r.status}</span>
                {r.hiddenForReview && (
                  <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold inline-flex items-center gap-1">
                    <Flag className="h-3 w-3" /> hidden for review
                  </span>
                )}
                <span className="ml-auto text-gray-400">{(r.createdAt || '').slice(0, 16).replace('T', ' ')}</span>
              </div>
              <p className="text-sm text-gray-800 mb-1.5">{r.body}</p>
              {Array.isArray(r.photos) && r.photos.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`admin-cr-photos-${r.id}`}>
                  {r.photos.slice(0, 3).map((url, idx) => (
                    <a
                      key={url}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="h-20 w-20 rounded-lg overflow-hidden border border-gray-200 bg-gray-50 block"
                      data-testid={`admin-cr-photo-${r.id}-${idx}`}
                    >
                      <img src={url} alt="" loading="lazy" className="h-full w-full object-cover" />
                    </a>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-3 text-[11px] text-gray-500 mb-2">
                <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> {r.confirmCount || 0} confirms</span>
                <span className="inline-flex items-center gap-1"><Flag className="h-3 w-3" /> {r.disputeCount || 0} disputes</span>
                <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" /> {r.replyCount || 0} replies</span>
                <span>author: {(r.authorName || '').trim() || r.authorUserId?.slice(0, 8)}</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap border-t border-gray-100 pt-2">
                {r.status !== 'removed' ? (
                  <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => remove(r.id)} data-testid={`admin-cr-remove-${r.id}`}>
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => restore(r.id)} data-testid={`admin-cr-restore-${r.id}`}>
                    <RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore
                  </Button>
                )}
                {r.hiddenForReview && r.status !== 'removed' && (
                  <Button size="sm" variant="outline" onClick={() => restore(r.id)} data-testid={`admin-cr-clear-flags-${r.id}`}>
                    <RotateCcw className="h-3.5 w-3.5 mr-1" /> Clear flags & restore
                  </Button>
                )}
                {['removed', 'expired', 'resolved'].includes(r.status) && (
                  <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={() => permDelete(r.id)} data-testid={`admin-cr-perm-delete-${r.id}`}>
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Permanently delete
                  </Button>
                )}
                {r.authorUserId && (
                  <Button size="sm" variant="outline" className="text-amber-700 border-amber-200" onClick={() => suspendAuthor(r.authorUserId, true)} data-testid={`admin-cr-suspend-${r.id}`}>
                    <Ban className="h-3.5 w-3.5 mr-1" /> Suspend author
                  </Button>
                )}
                {r.incidentId && (
                  <Button size="sm" variant="ghost" className="ml-auto" onClick={() => navigate(`/incidents/${r.incidentId}`)} data-testid={`admin-cr-open-incident-${r.id}`}>
                    <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open incident
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminCommunityReportsPanel;
